import { useCallback, useMemo } from 'react';

import { Button, Checkbox, IconButton, VStack, useDisclosure } from '@chakra-ui/react';
import { Formik } from 'formik';
import { RiMailSendLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  CustomDateKeyEnum,
  Deal,
  MessageTemplateKey,
  useCustomDatesUpdateMutation,
  useDealAssignMutation,
  useFillMessageTemplateMutation,
  useMessageSendMutation,
  useMessageTemplateByKeyQuery,
} from '../../../gql/generated/graphql';

import DatePicker from '../../shared/DatePicker';
import Modal from '../../shared/Modal';
import Tooltip from '../../shared/Tooltip';

import { useUser } from '../../../hooks/useUser';
import { logger } from '../../../libs/Logger';
import { formatPhone } from '../../../libs/utils';
import { RemoveIndex } from '../../../utils/types';

export const validationSchema = Yup.object().shape({
  temporary_registration_tags_expiration_date: Yup.date()
    .optional()
    .nullable()
    .required('Expiration Date is required'),
  send_text_to_customer: Yup.boolean().optional().nullable(),
});

export type FormValues = Partial<RemoveIndex<Yup.InferType<typeof validationSchema>>>;

type SendTempTagsProps = { deal: Deal };

const SendTempTags = ({ deal }: SendTempTagsProps) => {
  const user = useUser();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [updateCustomDatesMutation, { loading: loadingCustomDatesUpdate }] =
    useCustomDatesUpdateMutation();
  const [sendMessage, { loading: loadingSendMessage }] = useMessageSendMutation();
  const [fillMessageTemplate] = useFillMessageTemplateMutation();
  const [dealAssign, { loading: loadingDealAssign }] = useDealAssignMutation();

  const loading = loadingCustomDatesUpdate || loadingSendMessage || loadingDealAssign;

  const hasTrackingNumber = !!deal.shipping_labels?.[0]?.tracking_number;

  const { data: templateTextsData } = useMessageTemplateByKeyQuery({
    variables: { key: MessageTemplateKey.TitlingTemporaryTags },
  });
  const temporaryTagsTemplateText = useMemo(
    () => templateTextsData?.messageTemplateByKey,
    [templateTextsData],
  );

  const initialValues: FormValues = useMemo(
    () => ({
      temporary_registration_tags_expiration_date: deal.deal_dates?.custom_dates
        ?.temporary_registration_tags_expiration_date
        ? new Date(deal.deal_dates.custom_dates.temporary_registration_tags_expiration_date)
        : undefined,
      send_text_to_customer: false,
    }),
    [deal.deal_dates?.custom_dates?.temporary_registration_tags_expiration_date],
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (!deal.id || !deal.customer) {
        return;
      }

      try {
        await updateCustomDatesMutation({
          variables: {
            dealId: deal.id,
            dateKey: CustomDateKeyEnum.TemporaryRegistrationTagsExpirationDate,
            date: values.temporary_registration_tags_expiration_date,
          },
        });
        // `temporary_registration_tags_sent_date` should be updated in second place because it triggers the subscription.
        await updateCustomDatesMutation({
          variables: {
            dealId: deal.id,
            dateKey: CustomDateKeyEnum.TemporaryRegistrationTagsSentDate,
            // TODO: DateTime! is interpreted as any.
            // The fix is adding `DateTime: string | Date` to the `config.scalars` in `codegen.yml`. Several errors will be thrown.
            date: new Date().toISOString(),
          },
        });

        // Assign to the user that sent the temp tags.
        await dealAssign({
          variables: {
            deal_id: deal.id ?? 0,
            assignee_id: user.id,
            role: 'temporary_registration_tags_user_id',
            allow_reassign: true,
          },
        });

        if (values.send_text_to_customer) {
          if (!hasTrackingNumber) {
            toast.error('Tracking # not found. Please send manually.');
            onClose();
            return;
          }
          if (temporaryTagsTemplateText?.id == null) {
            toast.error('Template text not found. Please send manually.');
            onClose();
            return;
          }

          const recipientPhone = deal.customer.phone_number ?? '';
          if (!recipientPhone) {
            toast.error('Customer phone number not found. Please send manually.');
            onClose();
            return;
          }

          const { data: fillData } = await fillMessageTemplate({
            variables: {
              input: { templateId: temporaryTagsTemplateText.id, dealId: deal.id, recipientPhone },
            },
          });
          const message = fillData?.fillMessageTemplate.renderedMessage;
          if (message == null) {
            toast.error('Failed to render template. Please send manually.');
            onClose();
            return;
          }

          const formattedPhone = formatPhone(deal.customer.phone_number ?? '');
          await sendMessage({
            variables: {
              id: deal.id,
              dealPhoneNumber: formattedPhone,
              message,
            },
          });
        }

        toast.success('Deal assigned and Temp Tags sent successfully');
        onClose();
      } catch (e) {
        const error = e as Error;
        logger.error(
          'SendTempTags.tsx',
          'upsertVehicleInfo or updateCustomDatesMutation or sendMessage',
          values,
          error,
        );
        toast.error(
          `Error sending Temp Tags: ${error.message || error}. Please refresh and try again.`,
        );
      }
    },
    [
      deal.id,
      deal.customer,
      dealAssign,
      hasTrackingNumber,
      onClose,
      user.id,
      temporaryTagsTemplateText,
      updateCustomDatesMutation,
      sendMessage,
      fillMessageTemplate,
    ],
  );

  return (
    <>
      <IconButton
        aria-label="Send Temp Tags"
        variant="iconHover"
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        icon={<RiMailSendLine />}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnMount
      >
        {({ values, setFieldValue }) => (
          <Modal
            title="Send Temp Tags"
            hasForm
            isOpen={isOpen}
            onClose={onClose}
            leftButtons={
              <Button variant="warning" onClick={onClose} isDisabled={loading}>
                CANCEL
              </Button>
            }
            rightButtons={
              <Button type="submit" isLoading={loading} loadingText="SEND TAGS">
                SEND TAGS
              </Button>
            }
            scrollBehavior="outside"
          >
            <VStack spacing={4} align="flex-start">
              <DatePicker
                name="temporary_registration_tags_expiration_date"
                topLabel="Please enter the Expiration Date of the temporary tags"
              />
              <Tooltip
                label="A shipping label has not been created yet."
                isDisabled={hasTrackingNumber}
                placement="bottom"
                hasArrow
              >
                <div>
                  <Checkbox
                    name="send_text_to_customer"
                    size="sm"
                    isChecked={!!values.send_text_to_customer}
                    isDisabled={!hasTrackingNumber}
                    onChange={(e) => setFieldValue('send_text_to_customer', e.target.checked)}
                  >
                    Send tracking information text to customer
                  </Checkbox>
                </div>
              </Tooltip>
            </VStack>
          </Modal>
        )}
      </Formik>
    </>
  );
};

export default SendTempTags;
